import React, { useState, useEffect } from 'react'
import Topbar from '../layouts/Topbar'
import Sidebar from '../layouts/Sidebar'
import axios from 'axios'
import Cookies from 'js-cookie'
import { FaSearch, FaFilter, FaEye, FaEdit, FaCalendarAlt, FaUser, FaCheckCircle, FaTimesCircle, FaClock, FaCommentDots, FaSpinner } from 'react-icons/fa'
import './EmployeeImprovementPlan.css'
import API_BASE_URL from '../../../config/api'

const EmployeeImprovementPlan = () => {
  const base_url = API_BASE_URL
  const [improvementPlans, setImprovementPlans] = useState([])
  const [filteredPlans, setFilteredPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedYear, setSelectedYear] = useState('')
  const [availableYears, setAvailableYears] = useState([])
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [approvalModalOpen, setApprovalModalOpen] = useState(false)
  const [rejectionModalOpen, setRejectionModalOpen] = useState(false)
  const [approvalComment, setApprovalComment] = useState('')
  const [rejectionReason, setRejectionReason] = useState('')

  useEffect(() => {
    fetchImprovementPlans()
  }, [])

  useEffect(() => {
    filterPlans()
  }, [improvementPlans, searchQuery, selectedYear])

  const fetchImprovementPlans = async () => {
    try {
      setLoading(true)
      const token = Cookies.get('token')
      const response = await axios.get(`${base_url}/api/improvement-plans/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      console.log(response.data)
      if (response.data && response.data.length > 0) {
        setImprovementPlans(response.data)
        
        // Extract unique years and sort them, filtering out null/undefined values
        const years = [...new Set(response.data
          .map(plan => plan.year)
          .filter(year => year !== null && year !== undefined)
        )].sort((a, b) => b - a)
        setAvailableYears(years)
        
        // Set the most recent year as default
        if (years.length > 0) {
          setSelectedYear(years[0].toString())
        }
      } else {
        setImprovementPlans([])
      }
    } catch (error) {
      console.error('Error fetching improvement plans:', error)
      setError('Failed to fetch improvement plans')
    } finally {
      setLoading(false)
    }
  }

  const filterPlans = () => {
    let filtered = [...improvementPlans]

    // Filter by year
    if (selectedYear) {
      filtered = filtered.filter(plan => 
        plan.year !== null && 
        plan.year !== undefined && 
        plan.year.toString() === selectedYear
      )
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(plan => 
        (plan.employee_name && plan.employee_name.toLowerCase().includes(query)) ||
        (plan.employee_email && plan.employee_email.toLowerCase().includes(query)) ||
        (plan.strengths && plan.strengths.toLowerCase().includes(query)) ||
        (plan.weaknesses && plan.weaknesses.toLowerCase().includes(query)) ||
        (plan.employee_comment && plan.employee_comment.toLowerCase().includes(query)) ||
        (plan.training_needs && plan.training_needs.toLowerCase().includes(query)) ||
        (plan.supervisor_comment && plan.supervisor_comment.toLowerCase().includes(query))
      )
    }

    setFilteredPlans(filtered)
  }

  const handleViewPlan = (plan) => {
    setSelectedPlan(plan)
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setSelectedPlan(null)
  }

  const handleApprove = async () => {
    try {
      const token = Cookies.get('token')
      await axios.patch(`${base_url}/api/improvement-plans/${selectedPlan.id}/`, {
        supervisor_comment: approvalComment,
        approval_status: 'approved'
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      setApprovalModalOpen(false)
      setApprovalComment('')
      fetchImprovementPlans()
      closeModal()
    } catch (error) {
      console.error('Error approving plan:', error)
    }
  }

  const handleReject = async () => {
    try {
      const token = Cookies.get('token')
      await axios.patch(`${base_url}/api/improvement-plans/${selectedPlan.id}/`, {
        rejection_reason: rejectionReason,
        approval_status: 'rejected'
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      setRejectionModalOpen(false)
      setRejectionReason('')
      fetchImprovementPlans()
      closeModal()
    } catch (error) {
      console.error('Error rejecting plan:', error)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <FaCheckCircle className="status-icon approved" />
      case 'rejected':
        return <FaTimesCircle className="status-icon rejected" />
      case 'pending':
        return <FaSpinner className="status-icon pending" />
      default:
        return <FaSpinner className="status-icon pending" />
    }
  }

  const getStatusClass = (status) => {
    switch (status) {
      case 'approved':
        return 'eip-status-approved'
      case 'rejected':
        return 'eip-status-rejected'
      case 'pending':
        return 'eip-status-pending'
      default:
        return 'eip-status-pending'
    }
  }

  if (loading) {
    return (
      <>
      <Topbar/>
      <Sidebar/>
      <div className="employee-goals-container">
        <div className="loading-container">
          <FaSpinner className="spinner" />
          <p>Loading improvement plans...</p>
        </div>
      </div>
      </>
      
    )
  }

  if (error) {
    return (
      <>
      <Topbar/>
      <Sidebar/>
      <div className="employee-goals-container">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchImprovementPlans} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
      </>
      
    )
  }

  return (
    <>
      <Topbar />
      <Sidebar />
      <div className="employee-improvement-plans-container">
        {/* Page Header */}
        <div className="page-header">
          <div className="page-title">
            <h1>Employee Improvement Plans</h1>
            <p>Review and manage employee improvement plans</p>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="filters-section">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search by employee name, email, or content..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-container">
            <FaFilter className="filter-icon" />
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="year-filter"
            >
              
              {availableYears.map(year => (
                <option key={year} value={year.toString()}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Improvement Plans Table */}
        {filteredPlans.length === 0 ? (
          <div className="no-data-container">
            <div className="no-data-icon">
              <FaCommentDots />
            </div>
            <h3>No Improvement Plans Found</h3>
            <p>
              {searchQuery || selectedYear 
                ? 'Try adjusting your search criteria or filters.'
                : 'No improvement plans have been submitted yet.'
              }
            </p>
          </div>
        ) : (
          <div className="table-container">
            <table className="improvement-plans-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Year</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPlans.map((plan) => (
                  <tr key={plan.id} className="table-row">
                    <td className="employee-cell">
                      <div className="employee-info">
                        <FaUser className="employee-icon" />
                        <div className="employee-details">
                          <div className="employee-name">{plan.employee_name || 'Unknown Employee'}</div>
                          <div className="employee-email">{plan.employee_email || 'No email provided'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="year-cell">
                      <div className="year-badge">
                        <FaCalendarAlt />
                        <span>{plan.year || 'N/A'}</span>
                      </div>
                    </td>
                    
                    <td className="status-cell">
                      <div className={`status-badge ${getStatusClass(plan.approval_status)} `}>
                      <span className={plan.approval_status === 'pending' ? 'spinning' : ''}>
                      {getStatusIcon(plan.approval_status)}
                    </span>
                        &nbsp;&nbsp;
                        <span className="status-text">
                          {plan.approval_status === 'approved' ? 'Approved' :
                           plan.approval_status === 'rejected' ? 'Rejected' : 'Pending'}
                        </span>
                      </div>
                    </td>
                    
                    <td className="actions-cell">
                      <button
                        onClick={() => handleViewPlan(plan)}
                        className="view-btn"
                        title="View full details"
                      >
                        <FaEye />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal for viewing plan details */}
        {modalOpen && selectedPlan && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-title-section">
                  <h2>Improvement Plan Details</h2>
                  <div className="employee-badge">
                    <FaUser className="employee-icon" />
                    <span>{selectedPlan.employee_name || 'Unknown Employee'}</span>
                  </div>
                </div>
                <div className="modal-actions">
                  {selectedPlan.approval_status === 'pending' && (
                    <>
                      <button
                        onClick={() => setApprovalModalOpen(true)}
                        className="approve-btn"
                        title="Approve plan"
                      >
                        <FaCheckCircle />
                        Approve
                      </button>
                      <button
                        onClick={() => setRejectionModalOpen(true)}
                        className="reject-btn"
                        title="Reject plan"
                      >
                        <FaTimesCircle />
                        Reject
                      </button>
                    </>
                  )}
                  <button onClick={closeModal} className="close-btn">&times;</button>
                </div>
              </div>

              <div className="modal-body">
                
                <div className="plan-info-header">
                  <div className="plan-year-badge">
                    <FaCalendarAlt />
                    <span>Year: {selectedPlan.year || 'N/A'}</span>
                  </div>
                  <div className={`plan-status-badge ${getStatusClass(selectedPlan.approval_status)}`}>
                    <span className={selectedPlan.approval_status === 'pending' ? 'spinning' : ''}>
                      {getStatusIcon(selectedPlan.approval_status)}
                    </span>
                    &nbsp;&nbsp;
                    <span>
                      {selectedPlan.approval_status === 'approved' ? 'Approved' :
                       selectedPlan.approval_status === 'rejected' ? 'Rejected' : 'Pending Review'}
                    </span>
                  </div>
                </div>

                {selectedPlan.approval_status === 'rejected' && selectedPlan.rejection_reason && (
                  <div className="modal-section rejection-section">
                    <div className="section-header">
                      <h4>Rejection Reason</h4>
                    </div>
                    <div className="section-content">
                      <div className="rejection-reason">
                        <FaCommentDots className="reason-icon" />
                        <p>{selectedPlan.rejection_reason}</p>
                      </div>
                    </div>
                  </div>
                )}


                <div className="modal-sections">

                  <div className="modal-section strengths-section">
                    <div className="section-header">
                      <h4>Strengths</h4>
                    </div>
                    <div className="section-content">
                      <p>{selectedPlan.strengths || 'No strengths listed'}</p>
                    </div>
                  </div>

                  <div className="modal-section weaknesses-section">
                    <div className="section-header">
                      <h4>Areas for Improvement</h4>
                    </div>
                    <div className="section-content">
                      <p>{selectedPlan.weaknesses || 'No weaknesses listed'}</p>
                    </div>
                  </div>

                  <div className="modal-section comment-section">
                    <div className="section-header">
                      <h4>Training Needs</h4>
                    </div>
                    <div className="section-content">
                      <p>{selectedPlan.training_needs || 'No training needs specified'}</p>
                    </div>
                  </div>

                  <div className="modal-section comment-section">
                    <div className="section-header">
                      <h4>Employee Comments</h4>
                    </div>
                    <div className="section-content">
                      <p>{selectedPlan.employee_comment || 'No comment provided'}</p>
                    </div>
                  </div>

                  {selectedPlan.date_signed && (
                    <div className="modal-section date-section">
                      <div className="section-header">
                        <h4>Date Signed</h4>
                      </div>
                      <div className="section-content">
                        <p>{formatDate(selectedPlan.date_signed)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Approval Modal */}
        {approvalModalOpen && (
          <div className="modal-overlay" onClick={() => setApprovalModalOpen(false)}>
            <div className="modal-content comment-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Approve Improvement Plan</h2>
                <button onClick={() => setApprovalModalOpen(false)} className="close-btn">&times;</button>
              </div>
              <div className="modal-body">
                <div className="comment-section">
                  <label htmlFor="approval-comment">Supervisor Comment (Optional)</label>
                  <textarea
                    id="approval-comment"
                    className="comment-input"
                    value={approvalComment}
                    onChange={(e) => setApprovalComment(e.target.value)}
                    placeholder="Add any additional comments for the employee..."
                    rows="4"
                  />
                </div>
                <div className="modal-actions">
                  <button onClick={() => setApprovalModalOpen(false)} className="cancel-btn">
                    Cancel
                  </button>
                  <button onClick={handleApprove} className="submit-btn">
                    Approve Plan
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Rejection Modal */}
        {rejectionModalOpen && (
          <div className="modal-overlay" onClick={() => setRejectionModalOpen(false)}>
            <div className="modal-content comment-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Reject Improvement Plan</h2>
                <button onClick={() => setRejectionModalOpen(false)} className="close-btn">&times;</button>
              </div>
              <div className="modal-body">
                <div className="comment-section">
                  <label htmlFor="rejection-reason">Reason for Rejection *</label>
                  <textarea
                    id="rejection-reason"
                    className="comment-input"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Please provide specific reasons for rejection..."
                    rows="4"
                    required
                  />
                </div>
                <div className="modal-actions">
                  <button onClick={() => setRejectionModalOpen(false)} className="cancel-btn">
                    Cancel
                  </button>
                  <button 
                    onClick={handleReject} 
                    className="submit-btn reject-submit-btn"
                    disabled={!rejectionReason.trim()}
                  >
                    Reject Plan
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default EmployeeImprovementPlan